import { api } from "@/lib/api";
import {
  ApiResponse,
  ErrorResponse,
  Expense,
  Transaction,
  Transfer,
} from "@/lib/types";
import { useMutation } from "@tanstack/react-query";

export interface AddExpensePayload {
  transactionDate: string;
  amount: number;
  description?: string;
  reference: string;
  currencyId: string;
  budgetSubcategoryId?: string;
  vendorId: string;
  accountId: string;
  vesselId: string;
  accountConversionRate?: number;
}

export interface UpdateExpensePayload extends AddExpensePayload {
  id: string;
  version: number;
}

const toExpensePayload = (data: AddExpensePayload) => ({
  transactionDate: data.transactionDate,
  amount: data.amount,
  description: data.description ?? "",
  reference: data.reference ?? "",
  currencyId: data.currencyId,
  budgetSubcategoryId: data.budgetSubcategoryId,
  vendorId: data.vendorId,
  accountId: data.accountId,
  vesselId: data.vesselId,
  accountConversionRate: data.accountConversionRate,
});

const toUpdateExpensePayload = (data: UpdateExpensePayload) => ({
  ...toExpensePayload(data),
  version: data.version,
});

export const useAddExpenseTransaction = (props?: {
  onSuccess?: ({ data }: { data: ApiResponse<Expense> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationKey: ["expense-transactions"],
    mutationFn: async (formData: AddExpensePayload) => {
      return await api.post(
        "/transactions/expense",
        toExpensePayload(formData)
      );
    },
    ...props,
  });
};

interface AddAttachementToTransactionBody {
  file: File;
  transactionId: string;
  description?: string;
}

export const useCreateTransactionAttachment = (props?: {
  onSuccess?: (data: { data: string }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: AddAttachementToTransactionBody) => {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("description", data.description ?? "");

      return api.post(
        `/transactions/${data.transactionId}/attachments`,
        formData
      );
    },
    ...props,
  });
};

export const useDeleteTransactionAttachment = (props?: {
  onSuccess?: (data: { data: string }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: { transactionId: string; attachmentId: string }) => {
      return api.delete(
        `/transactions/${data.transactionId}/attachments/${data.attachmentId}`
      );
    },
    ...props,
  });
};

export const useUpdateTransaction = (props?: {
  onSuccess?: (data: { data: ApiResponse<Transaction> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: Transaction) => {
      const type = data.type.toLowerCase();
      return api.put(`/transactions/${type}/${data.id}`, data);
    },
    ...props,
  });
};

export const useUpdateExpense = (props?: {
  onSuccess?: (data: { data: ApiResponse<Expense> }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: UpdateExpensePayload) =>
      api.put(`/transactions/expense/${data.id}`, toUpdateExpensePayload(data)),
    ...props,
  });
};

export interface CreateTransferPayload {
  transactionDate: Date;
  amount: number;
  description: string;
  reference?: string;
  currencyId: string;
  vesselId: string;
  fromAccountId: string;
  toAccountId: string;
  fromAccountConversionRate?: number;
  toAccountConversionRate?: number;
  bankCommission?: {
    accountId: string;
    currencyId: string;
    amount: number;
    accountConversionRate?: number;
  };
}

const toTransferPayload = (data: CreateTransferPayload) => ({
  transactionDate: data.transactionDate,
  amount: data.amount,
  description: data.description ?? "",
  reference: data.reference ?? "",
  currencyId: data.currencyId,
  vesselId: data.vesselId,
  fromAccountId: data.fromAccountId,
  toAccountId: data.toAccountId,
  fromAccountConversionRate: data.fromAccountConversionRate,
  toAccountConversionRate: data.toAccountConversionRate,
  bankCommission: data.bankCommission,
});

export const useCreateTransfer = (props?: {
  onSuccess?: (data: ApiResponse<Transfer>) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation<
    ApiResponse<Transfer>,
    ErrorResponse,
    CreateTransferPayload
  >({
    mutationFn: async (data: CreateTransferPayload) => {
      const response = await api.post<ApiResponse<Transfer>>(
        "/transactions/transfer",
        toTransferPayload(data)
      );
      return response.data;
    },
    ...props,
  });
};

export interface UpdateTransferPayload extends CreateTransferPayload {
  id: string;
  version: number;
}

export const useUpdateTransfer = (props?: {
  onSuccess?: (data: ApiResponse<Transfer>) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation<
    ApiResponse<Transfer>,
    ErrorResponse,
    UpdateTransferPayload
  >({
    mutationFn: async (data: UpdateTransferPayload) => {
      const response = await api.put<ApiResponse<Transfer>>(
        `/transactions/transfer/${data.id}`,
        {
          ...toTransferPayload(data),
          version: data.version,
        }
      );
      return response.data;
    },
    ...props,
  });
};

export const useDeleteTransaction = (props?: {
  onSuccess?: (data: { data: string }) => void;
  onError?: (error: ErrorResponse) => void;
}) => {
  return useMutation({
    mutationFn: (data: { id: string }) =>
      api.delete(`/transactions/${data.id}`),
    ...props,
  });
};
